import { startOfDay } from 'date-fns';
import { UseFormSetValue } from 'react-hook-form';
import { Accident, AccidentExistingUserResponse, AccidentUserType, AresResponse } from '../../types/accident';
import { UserPrefix } from '../AccidenCreatingStepCustomerInformations';

export const isStkExpired = (stk?: string | Date | null, accidentDate?: string | Date | null): boolean => {
  if (!stk) return false;

  const stkDate = new Date(stk);
  const referenceDate = accidentDate ? new Date(accidentDate) : new Date();

  if (Number.isNaN(stkDate.getTime()) || Number.isNaN(referenceDate.getTime())) return false;

  return startOfDay(stkDate) < startOfDay(referenceDate);
};

export const toFormValues = (accident: Accident): Accident => ({
  ...accident,
  user: {
    ...accident.user,
    address: {
      ...accident.user.address,
      countryId: accident.user?.address?.country?.id ?? accident.user?.address?.countryId ?? null
    } as any
  },
  operator:
    accident.operator && Object.keys(accident.operator).length > 0
      ? ({
          ...accident.operator,
          address: accident.operator.address
            ? {
                ...accident.operator.address,
                countryId:
                  accident.operator?.address?.country?.id ?? (accident as any).operator?.address?.countryId ?? null
              }
            : undefined
        } as any)
      : undefined
});

export const applyAresData = (
  prefix: UserPrefix,
  data: AresResponse | undefined,
  setValue: UseFormSetValue<Accident>,
  userType?: AccidentUserType
) => {
  if (!data || data.code !== 200) return;

  const address = data.response.sidlo;
  const zip = address.psc.toString();
  const city = address.nazevMestskeCastiObvodu ?? address.nazevObce;
  const street = address.nazevUlice;
  const houseNum = [address.cisloDomovni, address.cisloOrientacni].filter(Boolean).join('/');

  setValue(`${prefix}.address.zip`, zip);
  setValue(`${prefix}.address.city`, city);
  setValue(`${prefix}.address.street`, street);
  setValue(`${prefix}.address.houseNum`, houseNum);

  if (userType === AccidentUserType.BusinessMan) {
    setValue(`${prefix}.company.companyIdNum`, data.response.ico);
    setValue(`${prefix}.company.companyTaxNum`, data.response.dic);

    const fullName = data.response.obchodniJmeno.trim();
    const titleRegex = /^(Ing\.|Mgr\.|MUDr\.|JUDr\.|PhDr\.|RNDr\.|Bc\.|Ph\.D\.|MBA|Ing\.-Paed\.IGIP|Doc\.|Prof\.)\s*/i;
    const titleMatch = fullName.match(titleRegex);

    const degree = titleMatch ? titleMatch[0].trim() : '';
    const remaining = fullName.replace(titleRegex, '').trim();
    const parts = remaining.split(' ');
    const name = parts[0] ?? '';
    const surname = parts.slice(1).join(' ');

    setValue(`${prefix}.degree`, degree);
    setValue(`${prefix}.name`, name);
    setValue(`${prefix}.surname`, surname);
  } else {
    // Firma
    setValue(`${prefix}.company.companyName`, data.response.obchodniJmeno);
    setValue(`${prefix}.company.companyTaxNum`, data.response.dic);
    setValue(`${prefix}.company.companyIdNum`, data.response.ico);
    setValue(`${prefix}.company.companyVatPayer`, data.response.seznamRegistraci.stavZdrojeDph === 'AKTIVNI');
  }
};

export const applyExistingData = (
  prefix: UserPrefix,
  data: AccidentExistingUserResponse | undefined,
  isCompany: boolean,
  setValue: UseFormSetValue<Accident>
) => {
  if (!data) return;
  setValue(`${prefix}.address.street`, data.street);
  setValue(`${prefix}.address.houseNum`, data.houseNum);
  setValue(`${prefix}.address.city`, data.city);
  setValue(`${prefix}.address.zip`, data.zip);
  setValue(`${prefix}.address.countryId`, data.countryId);

  if (isCompany) {
    setValue(`${prefix}.company.contactDegree`, data.degree);
    setValue(`${prefix}.company.companyName`, data.companyName);
    setValue(`${prefix}.company.contactName`, data.name);
    setValue(`${prefix}.company.contactSurname`, data.surname);
    setValue(`${prefix}.company.companyIdNum`, data.companyIdNum);
    setValue(`${prefix}.company.companyTaxNum`, data.companyTaxNum);
    setValue(`${prefix}.company.companyVatPayer`, data.companyVatPayer);
  } else {
    setValue(`${prefix}.degree`, data.degree);
    setValue(`${prefix}.name`, data.name);
    setValue(`${prefix}.surname`, data.surname);
  }
};
